/**
 * GiftReceivedLayer — Sprint 3 §3c (receiver side)
 * App-wide, sober inbox surface for knowledge gifts that arrived via a
 * `?gift=` deep link. Shows the most recent received gift as a dismissible
 * card; opening a spot just hands off to its map. Fully offline.
 */
import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getReceivedGifts, removeReceivedGift } from '../utils/giftManager';
import theme from '../styles/theme';

export default function GiftReceivedLayer() {
  const { t } = useTranslation('common');
  const [gifts, setGifts] = useState([]);

  const refresh = useCallback(() => setGifts(getReceivedGifts()), []);

  useEffect(() => {
    refresh();
    const onGift = () => refresh();
    window.addEventListener('wayfare-gift-received', onGift);
    return () => window.removeEventListener('wayfare-gift-received', onGift);
  }, [refresh]);

  const top = gifts[0];
  if (!top) return null;

  const d = top.data || {};
  const name = d.name || d.title || t('gift.aPlace');
  const isItinerary = top.type === 'itinerary';

  const openMaps = () => {
    if (d.mapsLink) { window.open(d.mapsLink, '_blank', 'noopener'); return; }
    if (typeof d.lat === 'number' && typeof d.lng === 'number') {
      window.open(`https://www.google.com/maps/search/?api=1&query=${d.lat},${d.lng}`, '_blank', 'noopener');
    }
  };

  const dismiss = () => setGifts(removeReceivedGift(top.id));

  const hasMap = !!d.mapsLink || (typeof d.lat === 'number' && typeof d.lng === 'number');

  return (
    <div style={S.wrap}>
      <div style={S.card}>
        <div style={S.eyebrow}>🎁 {top.from ? t('gift.fromSomeone', { name: top.from }) : t('gift.received')}</div>
        <div style={S.name}>{name}</div>
        {d.description && <div style={S.desc}>{d.description}</div>}
        {isItinerary && Array.isArray(d.stops) && (
          <div style={S.desc}>{t('gift.stopsCount', { count: d.stops.length })}</div>
        )}
        <div style={S.actions}>
          {hasMap && !isItinerary && (
            <button type="button" style={S.openBtn} onClick={openMaps}>{t('gift.openMap')}</button>
          )}
          <button type="button" style={S.dismissBtn} onClick={dismiss}>{t('gift.keepIt')}</button>
        </div>
      </div>
    </div>
  );
}

const S = {
  wrap: { position: 'fixed', left: 0, right: 0, bottom: 'calc(env(safe-area-inset-bottom, 0px) + 84px)', display: 'flex', justifyContent: 'center', padding: '0 16px', zIndex: 1250, pointerEvents: 'none' },
  card: { pointerEvents: 'auto', maxWidth: 420, width: '100%', background: theme.cream, border: `1px solid ${theme.terracottaPale}`, borderLeft: `3px solid ${theme.terracotta}`, borderRadius: 12, boxShadow: '0 6px 24px rgba(196,113,74,0.18)', padding: '14px 16px', animation: 'wf-whisper-in 360ms ease-out' },
  eyebrow: { fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase', color: theme.terracottaDark, fontWeight: 700, marginBottom: 4 },
  name: { fontSize: 16, fontWeight: 800, color: theme.brownDark },
  desc: { fontSize: 13, color: theme.brown, lineHeight: 1.4, marginTop: 4 },
  actions: { display: 'flex', gap: 10, marginTop: 12 },
  openBtn: { flex: 1, padding: '9px', borderRadius: 10, border: 'none', background: theme.terracotta, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  dismissBtn: { flex: 1, padding: '9px', borderRadius: 10, border: `1.5px solid ${theme.sand}`, background: 'transparent', color: theme.brown, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
};
